import * as Sentry from '@sentry/browser';

/**
 * Safely parse JSON from localStorage
 * @param {string} key - localStorage key
 * @param {any} [defaultValue=null] - value returned when the key is missing or unparsable
 * @returns {any} - parsed data or defaultValue if invalid
 */
export function safeParseLocalStorage(key, defaultValue = null) {
  let raw;
  try {
    raw = localStorage.getItem(key);
  } catch (error) {
    console.error(`Failed to read localStorage key "${key}":`, error);
    Sentry.captureException(error, {
      extra: { localStorageKey: key, context: 'safeParseLocalStorage' }
    });
    return defaultValue;
  }
  if (!raw) return defaultValue;
  try {
    const parsed = JSON.parse(raw);
    // A "[object Object]" anywhere in the raw value means an object was
    // stringified into the data by accident — structurally valid JSON but
    // semantically corrupt (never appears in legitimate data)
    if (raw.includes('[object Object]')) {
      throw new Error('Corruption marker "[object Object]" found in stored value');
    }
    return parsed ?? defaultValue;
  } catch (error) {
    console.error(`Failed to parse localStorage key "${key}":`, error);
    Sentry.captureException(error, {
      extra: { localStorageKey: key, context: 'safeParseLocalStorage' }
    });
    // Drop the corrupted entry so it can't poison future reads
    try {
      localStorage.removeItem(key);
    } catch (removeError) {
      console.error(`Failed to remove corrupted localStorage key "${key}":`, removeError);
    }
    return defaultValue;
  }
}

/**
 * Safely stringify + write JSON to localStorage
 * @param {string} key - localStorage key
 * @param {any} value - value to store
 * @returns {boolean} - true on success, false on failure
 */
export function safeSetLocalStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error(`Failed to write localStorage key "${key}":`, error);
    Sentry.captureException(error, {
      extra: { localStorageKey: key, context: 'safeSetLocalStorage' }
    });
    return false;
  }
}

/**
 * A player entry is valid when it is a well-formed record: an object with a
 * non-blank string name and a playIntervals array. Validation stays strict
 * so corruption is detected; repair stays lenient (it drops unusable
 * entries and backfills playIntervals) so it still recovers.
 */
function isValidPlayerEntry(entry) {
  if (!entry || typeof entry !== 'object') return false;
  if (typeof entry.name !== 'string' || entry.name.trim().length === 0) return false;
  // playIntervals may be absent (older records without interval tracking —
  // repair backfills []) but never malformed
  if (entry.playIntervals !== undefined && !Array.isArray(entry.playIntervals)) return false;
  return true;
}

/**
 * Validate pending match data structure
 * @param {any} data - data to validate
 * @returns {object} - validation result with isValid boolean and error message
 */
export function validatePendingMatchData(data) {
  if (!data) {
    return { isValid: false, error: 'Data is null or undefined' };
  }

  if (typeof data !== 'object') {
    return { isValid: false, error: 'Data is not an object' };
  }

  // Check for gameState property (required for proper structure)
  if (!data.gameState) {
    return { isValid: false, error: 'Missing gameState property' };
  }

  // Validate gameState structure
  if (typeof data.gameState !== 'object') {
    return { isValid: false, error: 'gameState is not an object' };
  }

  // Check for required gameState properties. Only the scores are mandatory —
  // goals / gameIntervals are defaulted to [] everywhere they are read, so
  // their absence means "no data yet", not corruption (repair fills them in).
  const requiredGameStateProps = ['ourScore', 'opponentScore'];
  for (const prop of requiredGameStateProps) {
    if (!(prop in data.gameState)) {
      return { isValid: false, error: `gameState missing required property: ${prop}` };
    }
  }

  // Scores must not be negative (type coercion is the repair step's job —
  // non-numeric scores are left for repair to normalise, not rejected here)
  const { ourScore, opponentScore } = data.gameState;
  if ((typeof ourScore === 'number' && ourScore < 0) || (typeof opponentScore === 'number' && opponentScore < 0)) {
    return { isValid: false, error: 'Invalid score values (negative scores)' };
  }

  // Unusually high scores are almost certainly a clock/timing bug — flag it
  // in the console but still accept the data
  if ((typeof ourScore === 'number' && ourScore > 100) || (typeof opponentScore === 'number' && opponentScore > 100)) {
    console.warn('Unusually high scores detected in match data');
  }

  // Check for playerData property
  if (!data.playerData) {
    return { isValid: false, error: 'Missing playerData property' };
  }

  // Validate playerData is an array
  if (!Array.isArray(data.playerData)) {
    return { isValid: false, error: 'playerData is not an array' };
  }

  // Every entry must be well-formed — nulls, blank names and malformed
  // playIntervals mean corruption (repair drops the unusable entries and
  // backfills playIntervals). An empty roster fails the same way: a match
  // with no identifiable players cannot be restored.
  if (data.playerData.length === 0 || !data.playerData.every(isValidPlayerEntry)) {
    return { isValid: false, error: 'No valid players found in playerData' };
  }

  return { isValid: true, error: null };
}

/**
 * Repair a single player entry. Returns the repaired entry, or null when
 * the entry is unrecoverable (null, or a blank/missing name).
 * Absent keys stay absent (so already-minimal records are untouched);
 * present-but-invalid values are coerced. playIntervals is backfilled to []
 * for parsed records, since playtime calculation expects the key.
 */
function repairPlayerEntry(entry, backfillIntervals = true) {
  if (!entry || typeof entry !== 'object') return null;
  const name = entry.name === undefined || entry.name === null ? '' : String(entry.name).trim();
  if (!name) return null;
  const repaired = { ...entry, name };
  if (backfillIntervals && !Array.isArray(repaired.playIntervals)) {
    repaired.playIntervals = [];
  }
  if (repaired.goalsScored !== undefined
      && (typeof repaired.goalsScored !== 'number' || repaired.goalsScored < 0)) {
    repaired.goalsScored = 0;
  }
  return repaired;
}

/**
 * Attempt to repair corrupted match data
 * @param {any} data - corrupted data to repair
 * @returns {object} - repair result with success and wasRepaired booleans, data, and error message
 */
export function attemptMatchDataRepair(data) {
  try {
    if (!data || typeof data !== 'object') {
      return { success: false, wasRepaired: false, error: 'Cannot repair non-object data', data: null };
    }

    console.log('Attempting to repair match data:', {
      hasGameState: !!data.gameState,
      hasPlayerData: !!data.playerData,
      dataKeys: Object.keys(data)
    });

    // Normalise the game state. When a gameState object exists, backfill
    // the required scores and preserve everything else. When it is missing
    // entirely, seed from legacy top-level properties when they exist
    // (older saved formats), otherwise fall back to fresh defaults.
    const hasGameStateObject = data.gameState && typeof data.gameState === 'object'
      && Object.keys(data.gameState).length > 0;
    const hasLegacyTopLevelScores = 'ourScore' in data || 'opponentScore' in data
      || 'goals' in data || 'gameIntervals' in data;

    let repairedGameState;
    // Filling in missing *required* scores counts as a repair; backfilling
    // optional defaults does not
    let repairedRequiredScores;
    if (hasGameStateObject) {
      repairedGameState = {
        ourScore: 0,
        opponentScore: 0,
        ...data.gameState,
      };
      repairedRequiredScores = !('ourScore' in data.gameState) || !('opponentScore' in data.gameState);
    } else if (hasLegacyTopLevelScores) {
      repairedGameState = {
        ourScore: data.ourScore ?? 0,
        opponentScore: data.opponentScore ?? 0,
        goals: data.goals ?? [],
        gameIntervals: data.gameIntervals ?? [],
        includeGKPlaytime: data.includeGKPlaytime ?? true,
      };
      repairedRequiredScores = true;
    } else {
      repairedGameState = {
        ourScore: 0,
        opponentScore: 0,
        isRunning: false,
        startTime: Date.now(),
        totalElapsed: 0,
      };
      repairedRequiredScores = true;
    }

    // playerData must be an array to repair. JSON-encoded arrays (double
    // encoded rosters) are decoded; comma-separated names are split; any
    // other non-array roster is unrecoverable.
    let rawPlayers = data.playerData;
    let playerDataWasString = false;
    let csvNamesOnly = false;
    if (typeof rawPlayers === 'string') {
      playerDataWasString = true;
      let parsed = null;
      try {
        const candidate = JSON.parse(rawPlayers);
        if (Array.isArray(candidate)) parsed = candidate;
      } catch {
        parsed = null;
      }
      if (parsed) {
        rawPlayers = parsed;
      } else if (rawPlayers.includes(',')) {
        rawPlayers = rawPlayers.split(',').map(name => ({ name: name.trim() }));
        csvNamesOnly = true;
      } else {
        return { success: false, wasRepaired: false, error: 'Cannot repair playerData that is not an array', data: null };
      }
    }

    if (rawPlayers !== undefined && !Array.isArray(rawPlayers)) {
      return { success: false, wasRepaired: false, error: 'Cannot repair playerData that is not an array', data: null };
    }
    const originalPlayers = Array.isArray(rawPlayers) ? rawPlayers : [];
    // CSV-derived names are already minimal records; parsed objects get
    // structural defaults backfilled
    const repairedPlayers = originalPlayers
      .map(entry => repairPlayerEntry(entry, !csvNamesOnly))
      .filter(Boolean);
    // Dropping unusable player entries counts as a repair; backfilling
    // defaults on kept entries is enrichment, like gameState defaults
    const droppedUnusablePlayers = repairedPlayers.length !== originalPlayers.length;

    if (repairedPlayers.length === 0) {
      return { success: false, wasRepaired: false, error: 'No valid players found to repair', data: null };
    }

    // Nothing needed repairing — hand back the original untouched
    if (!repairedRequiredScores && !droppedUnusablePlayers && !playerDataWasString && hasGameStateObject) {
      return { success: true, wasRepaired: false, data, error: null };
    }

    const repairedData = {
      ...data,
      gameState: repairedGameState,
      playerData: repairedPlayers,
    };

    // Validate the repaired data
    const repairedValidation = validatePendingMatchData(repairedData);
    if (repairedValidation.isValid) {
      console.log('Successfully repaired match data');
      return { success: true, wasRepaired: repairedRequiredScores || droppedUnusablePlayers || playerDataWasString, data: repairedData, error: null };
    } else {
      return { success: false, wasRepaired: false, error: `Repair failed validation: ${repairedValidation.error}`, data: null };
    }

  } catch (error) {
    console.error('Error during match data repair:', error);
    Sentry.captureException(error, {
      extra: { 
        originalData: data,
        context: 'attemptMatchDataRepair'
      }
    });
    return { success: false, wasRepaired: false, error: error.message, data: null };
  }
}

/**
 * Values that unambiguously mean "this entry is corrupt", as opposed to a
 * plain string (opponent names, flags) which must be left alone.
 */
const CORRUPTED_VALUE_MARKERS = new Set(['undefined', 'null', '', '[object Object]']);

/**
 * Keys whose values must be JSON. A bare string in one of these is unusable
 * (safeParse rejects it on read), so cleanup removes it proactively. Other
 * keys may legitimately hold plain strings (opponent names, flags), so only
 * exact corruption markers, embedded "[object Object]" and truncated JSON
 * count as corruption there.
 */
const JSON_VALUE_KEYS = new Set([
  'pending_match_data',
  'game_state',
  'player_data',
  'current_game_state',
  'players',
]);

function isCorruptedLocalStorageValue(key, value) {
  if (value === null || value === undefined) return false;
  if (typeof value !== 'string') return true;
  if (CORRUPTED_VALUE_MARKERS.has(value)) return true;
  if (value.includes('[object Object]')) return true;
  const trimmed = value.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      JSON.parse(trimmed);
      return false;
    } catch {
      return true;
    }
  }
  if (JSON_VALUE_KEYS.has(key)) {
    try {
      JSON.parse(value);
      return false;
    } catch {
      return true;
    }
  }
  return false;
}

/**
 * Clean up corrupted localStorage entries
 * @returns {number} - count of entries removed
 */
export function cleanupCorruptedLocalStorage() {
  const keysToCheck = [
    'pending_match_data',
    'game_state',
    'opponent_name',
    'player_data',
    'subscription_just_upgraded',
    'match_saved',
    'current_game_state',
    'players',
    'current_squad_id',
  ];
  let cleanedCount = 0;

  keysToCheck.forEach(key => {
    try {
      const data = localStorage.getItem(key);
      if (isCorruptedLocalStorageValue(key, data)) {
        console.warn(`Removing corrupted localStorage entry: ${key}`);
        localStorage.removeItem(key);
        cleanedCount++;
      }
    } catch (error) {
      // Isolate per-key access errors so one bad key can't break cleanup
      console.error(`Error checking localStorage key "${key}":`, error);
    }
  });

  if (cleanedCount > 0) {
    console.log('Cleaned up', `${cleanedCount} corrupted localStorage items`);
    Sentry.addBreadcrumb({
      message: `Cleaned up ${cleanedCount} corrupted localStorage entries`,
      level: 'info',
      category: 'localStorage',
    });
  }

  return cleanedCount;
}

/**
 * Format time in seconds to MM:SS format
 * @param {number} seconds - time in seconds
 * @returns {string} - formatted time string
 */
export function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Get up to two initials from a display name (first + last word).
 * "David Fox" -> "DF", "Pele" -> "P", "" -> "?".
 * @param {string} name - display name
 * @returns {string} - uppercase initials
 */
export function getInitials(name) {
  if (typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = Array.from(parts[0])[0] || '';
  const last = parts.length > 1 ? Array.from(parts[parts.length - 1])[0] || '' : '';
  return `${first}${last}`.toUpperCase();
}

/**
 * Deep clone an object
 * @param {any} obj - object to clone
 * @returns {any} - cloned object
 */
export function deepClone(obj) {
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (error) {
    console.error('Error deep cloning object:', error);
    Sentry.captureException(error, {
      extra: { obj, context: 'deepClone' }
    });
    return obj;
  }
}

/**
 * Debounce function to limit the rate of function execution
 * @param {Function} func - function to debounce
 * @param {number} wait - wait time in milliseconds
 * @returns {Function} - debounced function
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Check if a value is empty (null, undefined, empty string, empty array, empty object)
 * @param {any} value - value to check
 * @returns {boolean} - true if empty
 */
export function isEmpty(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}